/* ---------------------------------------------------------------------------
   What this library does differently from the 2009 workbooks, and why.

   THE HANDOVER'S ONE HARD RULE. "Stay faithful to CBPLR; correct only the
   recorded errors, and note each correction." A rating is a document an engineer
   signs and a posting sign is a legal instrument, so a web application that
   quietly produces a different number from the workbook the Department has used
   since 2009 is worse than one that reproduces a known defect. Every divergence
   is therefore declared here, printed on the rating report, and pinned by a test
   that asserts BOTH values — the workbook's and this one's — so the difference is
   documented rather than lost.

   `applied: false` is not a backlog. It means the workbook's behaviour is
   reproduced deliberately, and the entry exists so nobody later "fixes" it and
   silently breaks agreement with 20,245 lines of reference values.

   WHERE THESE CAME FROM. Six were recorded in Naveed's study of the workbooks
   (CBPLR-Understanding.md section 4). The rest were found while reproducing the
   reference values here. C7, C8 and C16 are the same species: a formula that
   reaches for the wrong cell and is numerically invisible in the one workbook it
   ships in, because the wrong cell happens to hold nearly the right number. Those
   are the dangerous ones. They are correct for this bridge and wrong for the next.
   C19 is worse than any of them and is the first thing to raise about PCDG: it is
   wrong for EVERY bridge, by arithmetic coincidence rather than by luck of one
   cell, and it sets the tendon area that every moment capacity in the family is
   built on.
   --------------------------------------------------------------------------- */

/** Which of the five families a finding belongs to, or all of them. */
export type Family = 'RCFS' | 'RCDG' | 'PCDG' | 'SIG' | 'RCDECK' | 'all';

export interface Correction {
  /** Stable id. Quoted on the rating report and in the tests. */
  id: string;
  family: Family;
  /** Where in the 2009 workbook, as `SHEET!CELLS`. */
  where: string;
  /** What the workbook does. */
  workbook: string;
  /** Why that is wrong, or why it is merely unclean. */
  fault: string;
  /**
   * Whether this library departs from the workbook.
   *
   * `false` means reproduced faithfully on purpose. See the header.
   */
  applied: boolean;
  /** What changes in the answer, in numbers where there are numbers. */
  effect: string;
}

export const CORRECTIONS: readonly Correction[] = [
  {
    id: 'C1',
    family: 'RCDECK',
    where: 'POSTLL!H41:H46',
    workbook:
      'The six cantilever-slab posting rating factors divide the CONTINUOUS slab capacity and dead load ($I$20, $I$21) by the cantilever live loads (I27:I29).',
    fault:
      'A cantilever rating computed from a continuous slab. The cantilever figures are one row group away, at $I$25 and $I$26, and are what these rows are labelled as using.',
    applied: true,
    effect:
      'In the reference workbook the cantilever is thicker (200 mm against 180 mm) but carries far more dead load (5.558 against 1.957 kN-m/m), so the workbook is optimistic. The sign of the error is not general — it follows whichever way the two slabs differ — so it cannot be dismissed as conservative.',
  },
  {
    id: 'C2',
    family: 'RCDECK',
    where: 'POSTSUM!H12:H15',
    workbook:
      'The cantilever posting rows take their vehicle weight from C7:C10, the CONTINUOUS slab block, rather than from their own C12:C15.',
    fault:
      'A reference into the wrong block. Harmless today only because the two blocks hold the same four weights, which is a coincidence of layout and not a fact about posting.',
    applied: true,
    effect:
      'No change to any reference value. Removing it costs nothing and removes a trap: edit one block and the other silently follows.',
  },
  {
    id: 'C3',
    family: 'RCDG',
    where: 'CCASM!G33:G34 (yinput / ycalculate)',
    workbook:
      'The ASM neutral axis of the T-section is circular: ycalculate depends on bw*y and y/2+ts, which depend on yinput, which the user retypes from ycalculate until the two agree.',
    fault:
      'Hand-cranked convergence. Nothing records whether the user actually converged it, so an unconverged sheet reads exactly like a converged one.',
    applied: true,
    effect:
      'Solved here in closed form — the working-stress neutral axis of a T-section is a quadratic — and the result is asserted against the workbook value. No manual step, and no way to leave it half done.',
  },
  {
    id: 'C4',
    family: 'PCDG',
    where: 'DSC!I19 (cinput)',
    workbook:
      'The bonded-tendon neutral axis is circular in the same way: the slab/girder averaged beta depends on c, and c depends on beta. The user retypes c.',
    fault:
      'The same hand-cranked convergence, on a stiffer loop — the reference workbook is left at c = 231.2 mm against a computed 231.165 mm, so it ships not quite converged.',
    applied: true,
    effect:
      'Iterated internally to a fixed tolerance. Reproduces 231.165 mm; the workbook rounds it to 231.2, which moves the moment capacity in the fourth significant figure.',
  },
  {
    id: 'C5',
    family: 'all',
    where: 'DFDLLL!B77:G106 and POSTLL!B33',
    workbook:
      'The printed span/moment reference table writes Type 1-1 as (83.5/L)*((L-0.853)/2)*((L-0.8534)/2), mixing two values of the same axle spacing. Separately, the posting sheets label MS18 as "MS18(HS29)".',
    fault:
      'Both cosmetic, and worth stating precisely because the study notes leave it open. NEITHER is on the calculation path: the rated live load comes from I43:I56 and I60:I73, which use 0.853 consistently, and the label is a label. The typo affects only the printed table, by about 0.02 kN-m at L = 8.5 m.',
    applied: true,
    effect:
      'One value of the spacing, 0.853 m, everywhere. MS18 is called MS18. No reference value moves.',
  },
  {
    id: 'C6',
    family: 'all',
    where: 'every workbook',
    workbook:
      'Load factors, the three posting vehicle weights, the material-by-era tables and the allowable stresses are typed as literals into each of the sixteen workbooks.',
    fault:
      'Sixteen near-duplicate copies of the same standard. A revision to the MLRB means sixteen edits, and the one nobody makes is the one somebody is rating a bridge with.',
    applied: true,
    effect:
      'One parameterised standards profile — see profile.ts. DPWH/CBPLR is the first; the numbers are identical to the workbooks and every rating records which profile version produced it.',
  },
  {
    id: 'C7',
    family: 'RCDECK',
    where: 'CCLFM!I27:I28',
    workbook:
      'The ultimate moment is computed as Uf*As*fy*(d - I20/2), where I20 is the CONCRETE COVER. The rectangular stress block depth a, which belongs in that expression, is computed two rows above at I22 and never used.',
    fault:
      'Found here, not in the study notes. Mu = phi*As*fy*(d - a/2) is the formula on every other sheet in the program; this one substitutes cover for a. It is numerically invisible in the reference workbook because a = 25.213 mm and the cover is 25 mm — agreement to three significant figures, by coincidence. Change the bar diameter, the spacing or f-prime-c and the two diverge with nothing to warn the user.',
    applied: true,
    effect:
      'a is used. Continuous-slab Mu moves from 53.7006 to 53.6835 kN-m/m — 0.03%, which is why it was never noticed. At 20 mm bars at 100 mm centres in the same slab a is about 47 mm, and the workbook would overstate Mu by around 1.5%.',
  },
  {
    id: 'C8',
    family: 'all',
    where: 'CCASM!H9 and H12 (RCFS; the same pair on RCDG and PCDG)',
    workbook:
      'The INVENTORY allowable stresses are looked up from the material-by-era table against the year built (H8, H10, H11). The OPERATING allowable stresses beside them — 10.3 MPa concrete and 193.1 MPa steel — are typed as literals.',
    fault:
      'Found here, not in the study notes. Half a lookup. The literals match the table for the reference bridge (1958, so the pre-1959 row), so the sheet is right — and stays right-looking after somebody changes the year built, at which point inventory follows the era table and operating does not.',
    applied: true,
    effect:
      'Both ratings read the same row of the same table. No reference value moves for the 1958 bridge; a 1985 bridge rated operating would have used 10.3 MPa instead of 18.6 MPa, understating the operating rating by around 45%.',
  },
  {
    id: 'C9',
    family: 'all',
    where: 'LRLFM!I16:J17 against POSTLFM!D22:J25',
    workbook:
      'The load-rating sheet rounds the rating factor to two decimals and multiplies the ROUNDED factor by the vehicle weight. The posting sheet, computing the same quantity, does not round at all.',
    fault:
      'Found here. The two sheets disagree about the same bridge: RCFS Simple is reported at LR 40.26 t on LRLFM and 40.134 t on POSTLFM, and POSTSUM — the sheet that decides the sign — uses the unrounded one.',
    applied: true,
    effect:
      'Carried at full precision throughout and rounded once, for display. The reported figure is POSTSUM’s, 40.134 t, because that is the number the posting decision was already made on.',
  },
  {
    id: 'C10',
    family: 'all',
    where: 'DFDLLL!J15, DFDLLL!I43:I73 and CCLFM!J33',
    workbook:
      'Three intermediate values are rounded and the rounded value is carried forward: the total dead load per metre to 2 decimals (14.45 kN/m from 14.4507), every live-load table lookup to 2 decimals (154.84 kN-m from 154.8448), and the stress-block depth a to the nearest whole millimetre (105 mm from 104.55).',
    fault:
      'Not a defect. It is recorded because it is LOAD-BEARING: reproducing the reference values requires rounding in exactly these places, and a library that carried full precision here would disagree with every workbook in the pack.',
    applied: false,
    effect:
      'Reproduced. Carrying a unrounded would give Mu = 463.03 rather than 462.918 kN-m; carrying the live-load lookup unrounded would move the inventory rating factor from 1.2162 to 1.2161.',
  },
  {
    id: 'C11',
    family: 'RCDG',
    where: 'DFDLLL, exterior girder distribution factor',
    workbook: 'The workbooks use 1 + (S - 1.829)/S. The MLRB text gives (S - 1.829)/S.',
    fault:
      'Open, and not ours to close. The two differ by exactly 1.0 wheel line on every exterior girder — for S = 2.4 m, 1.238 against 0.238, a factor of five on the live load. This is an engineering ruling, referred to Naveed in the handover.',
    applied: false,
    effect:
      'Neither is hard-coded. The profile carries the rule as a named choice, defaulting to the workbook form so the reference values reproduce, and every rating records which form it used. Nothing in this library will silently pick one.',
  },
  {
    id: 'C12',
    family: 'all',
    where: 'CCASM!H8 and H10 (RCFS; the same pair on RCDG and PCDG)',
    workbook:
      'The concrete era lookup is `IF(year < 1959, row1, row2)` — a two-way branch. The table printed directly beneath it has four rows: prior to 1959, after 1959, 1977 to 1981, after 1981. The last two are referenced by no formula anywhere in the workbook. The rebar table is the same shape, and its allowable stress is not looked up at all: it comes from whatever was typed on GENERAL.',
    fault:
      'Either a truncated lookup or a deliberate restriction, and the workbook does not say which. The two unreferenced rows carry a `*pc` mark that the other two do not, so they may apply to a material class the user is expected to select by hand. Against that: SIG’s equivalent tables have four real year bands each and are used as such, which is what a complete lookup looks like in this same program.',
    applied: false,
    effect:
      'Not resolved here, and not silently decided either. The full four-row tables are carried as data (profile.ts) and the engine takes the material values it is given, so the application can offer the era row, name it as the source, and let an engineer override it. A 1990 cast-in-situ deck rated by the workbook uses f-prime-c 20.7 where the table’s own last row says 31. Whether that is right is a question for the Department, like C11.',
  },
  {
    id: 'C13',
    family: 'SIG',
    where: 'LRASM!H12:H17',
    workbook:
      'The four allowable stresses for structural steel are typed as literals: 265 and 361.5 MPa in bending, 160.7 and 216.9 MPa in web shear.',
    fault:
      'A clarification rather than a defect, and it corrects the study notes, which read these as coming from the era table. They do not — the era table’s "after 1963" row gives 137.9 and 186.2. The reference bridge has fy = 482 MPa typed on GENERAL, and 265, 361.5, 160.7 and 216.9 are 0.55, 0.75, 0.333 and 0.45 times that, exact to four significant figures on all four. The rule is a fraction of fy; the era table is the fallback for steel whose grade is unknown, which is what bands like "Prior to 1905" are for.',
    applied: false,
    effect:
      'The fraction rule is implemented and the era table is offered as the fallback, so the reference values reproduce either way. What is genuinely open is which rule applies to a bridge whose fy is known and is not 482 MPa. TO BE CONFIRMED against the MLRB before the SIG family is released.',
  },
  {
    id: 'C14',
    family: 'all',
    where: 'DFDLLL!J13',
    workbook:
      'The barrier rail dead load is Wbr/bw * 1.25 — one rail’s weight spread over the full slab width, times 1.25.',
    fault:
      'Reproduced, origin not recorded. The 1.25 is not two rails, which would be 2.0, and it is not a load factor, since dead load is factored at 1.3 two sheets later. Neither the workbook nor the study notes say what it is. It contributes 2.71 of the reference bridge’s 14.45 kN/m, so it is not negligible.',
    applied: false,
    effect:
      'Carried as written, because it is on the reference path for every RC family and changing it would move every rating in the pack. Named here so the report states it rather than burying it, and flagged for confirmation against the MLRB.',
  },
  {
    id: 'C15',
    family: 'RCDG',
    where: 'CCASM!B63:E66',
    workbook:
      'The centroid of the compression zone, z, is built from three areas and their lever arms so that a T-section is treated differently from a rectangle. Those areas are then weighted by width — bw for the part in the web, beff for the parts in the flange — when the moment capacity Mc is formed at H37.',
    fault:
      'Found here. The width weighting is applied to the FORCE and not to the CENTROID: z comes from the unweighted areas. Worked through, the three terms sum to kd/2 and their moments to kd² / 6, so z is exactly kd/3 — the rectangular answer — for every section, however far the neutral axis runs into the web. The whole T-section block is algebraically inert. It is invisible in the reference workbook because that bridge is the rectangular case anyway (kd 159.8 mm in a 180 mm slab), where kd/3 is simply correct.',
    applied: false,
    effect:
      'Reproduced deliberately, and referred to the Department. Where the neutral axis does reach the web, the width-weighted centroid sits higher: for a 306 mm neutral axis in an 80 mm slab, z is 57.6 mm rather than 102.0, so the workbook understates the lever arm by 7.2% and understates BOTH the concrete and the reinforcement moment capacity by the same. That is conservative, which is why it is not corrected on our own authority — raising a published rating is not a change this library should make unasked. Unlike C7 and C8 it is not a slip with an obviously intended form elsewhere in the program; it is a question about what the method means, and it belongs beside C11 and C12.',
  },
  {
    id: 'C16',
    family: 'PCDG',
    where: 'LAYOUT!I61:I67 on the INTERIOR sheet',
    workbook:
      'The second-moment column of the section table reaches ONE ROW DOWN for every term in the upper flange: I61 computes the top flange’s I0 from row 62’s dimensions, I62 from row 63’s, I63 from row 64’s — and I63 and I64 then use the SAME row with different divisors, /12 and /36, which cannot both describe one shape.',
    fault:
      'Found here, and it is the C7 species again — a formula reaching for the wrong cell — except that this time the program contains its own counter-example. The EXTERIOR PCDG sheet tabulates the identical girder with every term taken from its own row. So the same girder has two different second moments in one family: Iz = 0.213746167860755 m4 on the exterior sheet against 0.213581121893672 on the interior. Both cannot be the section.',
    applied: true,
    effect:
      'The exterior sheet’s form is used, because it is the program’s own and needs no authority of ours. The exterior workbook then reproduces to full double precision on every value it publishes. The interior workbook diverges: girder Iz by 0.077%, composite Iz from 0.419120229725971 to 0.419285275693054, the serviceability compression rating from 6.44267029443497 to 6.446212464097575 and the tension rating from 1.25158935986086 to 1.252661973541179. Small, and in the direction of a slightly HIGHER rating, which is why it is stated here rather than waved through.',
  },
  {
    id: 'C17',
    family: 'PCDG',
    where: 'CCMS!H11',
    workbook:
      'A cell labelled Φ, holding 1, presented among the moment-capacity inputs.',
    fault:
      'No formula in the workbook reads it. The ultimate moment at H20 takes E44 or E46 directly, and neither multiplies by H11. The flexural capacity therefore carries no capacity-reduction factor at all, and an engineer who types 0.9 into the cell provided for it sees the rating not move.',
    applied: false,
    effect:
      'Reproduced: no factor is applied. It changes nothing while the cell holds 1, and that is exactly why it is dangerous rather than harmless — the sheet offers a control that does nothing. Applying `profile.capacityFactor` would drop every PCDG rating by ten per cent, which is not a change this library may make on its own authority. Referred to the Department beside C11, C12 and C15.',
  },
  {
    id: 'C18',
    family: 'PCDG',
    where: 'LAYOUT!E60 and the composite totals',
    workbook:
      'The composite section takes the deck slab at its GROSS area — full width times thickness — and adds it to the girder without any modular-ratio transformation, although the deck is 24.2 MPa concrete and the girder 38 MPa on the reference bridge.',
    fault:
      'A question about the method rather than a slip: composite section properties are normally formed on a deck transformed by n = Ec(slab)/Ec(girder). Nothing in the workbook or the study notes says whether the omission is deliberate.',
    applied: false,
    effect:
      'Reproduced, and referred to the Department. Taking n as sqrt(24.2/38) = 0.798, the transformation would move the composite Iz from 0.419285 to 0.391865 m4 — the workbook overstates it by 7.0% — and raise the soffit stress per unit moment by 2.66%. That flows straight into the three serviceability ratings, one of which governs this bridge, so it is not a rounding question. Not corrected here for the same reason as C15: it lowers a published rating, and that is the Department’s call.',
  },
  {
    id: 'C19',
    family: 'PCDG',
    where: 'CCMS!H8',
    workbook:
      'The assumed total prestressing loss is computed as `=GENERAL!G23*0.03` — three per cent of the WORKING FORCE, a quantity in kilonewtons — and the result, 158.1, is labelled MPa and used as a stress at H12 to find the tendon area.',
    fault:
      'Dimensionally incoherent, and on the critical path: H12 sets As*, which sets the moment capacity of the whole family. Three per cent of a force is not a stress, and 158.1 MPa happens to be 11.3% of 0.75 fu on this bridge, which matches no stated loss model. Double the prestressing force and the assumed loss doubles with it, for no physical reason.',
    applied: false,
    effect:
      'Reproduced exactly, because the reference tendon area of 4260.65 mm2 and every capacity built on it depend on it, and because what the loss SHOULD be is a question with an answer the workbook does not contain. THE FIRST THING TO ASK THE DEPARTMENT about this family: unlike C17 and C18 it is wrong for every bridge except by coincidence, and it is invisible on the sheet because the cell shows a plausible number.',
  },
] as const;

/** The corrections that bear on a given family's rating. For the report. */
export function correctionsFor(family: Family): readonly Correction[] {
  return CORRECTIONS.filter((c) => c.family === family || c.family === 'all');
}

export function correctionById(id: string): Correction | null {
  return CORRECTIONS.find((c) => c.id === id) ?? null;
}
